import { createSelector } from "reselect";
import { get } from "lodash";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  filterByClients,
  getExchangesToClientsMapping,
} from "@shared/features/filters/utils";
import {
  consolidatePortfolioChartData,
  formatTimeSeriesChartData,
} from "@shared/utils/charts";
import { portfolioFilters } from "@shared/slices/filters.slice";
import {
  ConsolidatedAccountData,
  ConsolidatedCoinData,
  GetAccountExtraInfoGeneralRequest,
  GetAmountsHistoryResponse,
  GetCoinExtraExchangesDataRequest,
  GetPortfolioByAccountRequest,
  ProcessedCoinExchangesData,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { QueryStatus, SkipToken } from "@reduxjs/toolkit/query";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import {
  dedupeAccountsData,
  getConsolidatedPortfolioData,
  preprocessPortfolioData,
  updateCoinRatios,
} from "@shared/features/portfolio/utils";

export const selectAccountsLoadingStates = createSelector(
  sharedLib.sharedApi.configuration.endpoints.getClientsList.select(undefined),
  sharedLib.sharedApi.portfolio.endpoints.getPortfolioAmount.select(undefined),
  (getClientsListQuery, getPortfolioAmountQuery) => {
    return {
      loading:
        getClientsListQuery.status === QueryStatus.pending ||
        getPortfolioAmountQuery.status === QueryStatus.pending,
      loaded:
        getClientsListQuery.status === QueryStatus.fulfilled ||
        getPortfolioAmountQuery.status === QueryStatus.fulfilled,
    };
  }
);
export const selectPortfolioDataLoadingStates = createSelector(
  sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.select(undefined),
  sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.select({
    use_cache: false,
  }),
  (withCacheData, noCacheData) => {
    return {
      loading: withCacheData.isLoading || noCacheData.isLoading,
      loaded: withCacheData.isSuccess || noCacheData.isSuccess,
    };
  }
);
export const selectPortfolioData = createSelector(
  sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.select(undefined),
  sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.select({
    use_cache: false,
  }),
  (cacheQueryStatus, noCacheQueryStatus) => {
    const queryStatus =
      (noCacheQueryStatus.fulfilledTimeStamp || 0) >
      (cacheQueryStatus.fulfilledTimeStamp || 0)
        ? noCacheQueryStatus
        : cacheQueryStatus;
    return queryStatus.data;
  }
);

export const selectPortfolioByAccount = (
  args: GetPortfolioByAccountRequest | SkipToken
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioByAccount.select(args),
    ({ data }) => data
  );

export const selectPortfolioByAccountLoadingStates = (
  args: GetPortfolioByAccountRequest | SkipToken
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioByAccount.select(args),
    (data) => {
      return {
        loading: data.isLoading,
        loaded: data.isSuccess,
      };
    }
  );

export const selectExchangesToClientsMapping = createSelector(
  selectPortfolioData,
  (portfolioData) => getExchangesToClientsMapping(portfolioData?.filters)
);

export const selectClientToExchangeMapping = createSelector(
  selectExchangesToClientsMapping,
  (exchangesToClientsMapping): { [key: string]: string } =>
    Object.entries(exchangesToClientsMapping).reduce(
      (acc: Record<string, string>, [exchange, clients]) => {
        clients?.forEach((client) => {
          acc[client] = exchange;
        });
        return acc;
      },
      {}
    )
);
export const selectPreprocessedPortfolioData = createSelector(
  selectPortfolioData,
  selectExchangesToClientsMapping,
  (portfolioData, exchangesToClientsMapping) => {
    return preprocessPortfolioData(portfolioData, exchangesToClientsMapping);
  }
);

export const selectFilteredPortfolioData = createSelector(
  selectPreprocessedPortfolioData,
  portfolioFilters.selectors.selectClientsFilter,
  (preprocessedPortfolioData, clientsFilter) => {
    return filterByClients(preprocessedPortfolioData, clientsFilter);
  }
);

export const selectConsolidatedPortfolioData = createSelector(
  selectFilteredPortfolioData,
  selectPortfolioData,
  (filteredPortfolioData, rawData) => {
    return updateCoinRatios(
      getConsolidatedPortfolioData(
        filteredPortfolioData,
        rawData?.client_dependency_new
      )
    ) as ConsolidatedCoinData[];
  }
);

export const selectConsolidatedPortfolioByAccountData = (
  args: GetPortfolioByAccountRequest | SkipToken
) =>
  createSelector(selectPortfolioByAccount(args), (portfolioData) => {
    let consolidatedCoinData: ConsolidatedCoinData[] = [];
    if (portfolioData?.data) {
      let { available: _, total: __, ...exchanges } = portfolioData.data;
      const [exchange] = Object.keys(exchanges);
      // An account without holdings comes back without an exchange key.
      if (!exchange || !exchanges[exchange]) return consolidatedCoinData;

      let { available: ___, total: ____, ...accounts } = exchanges[exchange];
      const exchangesToClientsMapping = { [exchange]: Object.keys(accounts) };

      consolidatedCoinData = getConsolidatedPortfolioData(
        preprocessPortfolioData(portfolioData, exchangesToClientsMapping),
        portfolioData?.client_dependency_new
      ) as ConsolidatedCoinData[];
    }
    return consolidatedCoinData;
  });

export const selectConsolidatedAccountsPortfolioData = createSelector(
  selectPortfolioData,
  selectExchangesToClientsMapping,
  portfolioFilters.selectors.selectClientsFilter,
  (portfolioData, exchangeToClientsMapping, clientsFilter) => {
    const data: ConsolidatedAccountData[] = [];
    Object.entries(exchangeToClientsMapping).forEach(([exchange, clients]) => {
      clients?.forEach((client) => {
        const rawAccountData = portfolioData?.data?.[exchange]?.[client];
        if (rawAccountData && clientsFilter.includes(client)) {
          data.push({
            total_ratio: rawAccountData.total.ratio!,
            total_in_btc: rawAccountData.total.BTC!,
            total_in_usd: rawAccountData.total.USD!,
            graph_data: rawAccountData.total.graph_data,
            available_in_btc: rawAccountData.available.BTC!,
            available_in_usd: rawAccountData.available.USD!,
            client,
            exchange: exchange,
            client_uuid: rawAccountData.uuid,
            client_id: rawAccountData.id,
          });
        }
      });
    });
    return data;
  }
);

export const selectUniqueAccountsPortfolioData = createSelector(
  selectPortfolioData,
  selectConsolidatedAccountsPortfolioData,
  (rawPortfolioData, accountsData) => {
    return dedupeAccountsData(
      accountsData,
      rawPortfolioData?.client_dependency_new
    );
  }
);

export const selectPortfolioPrecision = createSelector(
  selectPortfolioData,
  (portfolioData) => portfolioData?.precision
);

export const selectPortfolioByAccountPrecision = (
  args: GetPortfolioByAccountRequest | SkipToken
) =>
  createSelector(
    selectPortfolioByAccount(args),
    (portfolioData) => portfolioData?.precision
  );

// Portfolio amounts history graph selectors
export const selectPortfolioHistoryCache = createSelector(
  portfolioFilters.selectors.selectTimeRangeFilter,
  (state: any) => state,
  (portfolioTimeRangeFilter, state) =>
    sharedLib.sharedApi.portfolio.endpoints.getAmountsHistory.select({
      time_range: portfolioTimeRangeFilter,
    })(state)
);

export const selectPortfolioHistoryData = createSelector(
  selectPortfolioHistoryCache,
  ({ data }): GetAmountsHistoryResponse | undefined => data
);

export const selectPortfolioHistoryGraphPrecision = createSelector(
  selectPortfolioHistoryData,
  (portfolioHistoryData) => portfolioHistoryData?.precision
);

export const selectConsolidatedPortfolioHistoryData = createSelector(
  selectPortfolioHistoryData,
  portfolioFilters.selectors.selectTimeRangeFilter,
  portfolioFilters.selectors.selectClientsFilter,
  portfolioFilters.selectors.selectExchangeToClientsMapping,
  (
    portfolioHistoryData,
    portfolioTimeRangeFilter,
    portfolioClientsFilter,
    exchangeToClientsMapping
  ) =>
    consolidatePortfolioChartData(
      portfolioHistoryData,
      portfolioTimeRangeFilter,
      portfolioClientsFilter,
      exchangeToClientsMapping
    )
);

export const selectFormattedPortfolioHistoryGraphData = createSelector(
  selectConsolidatedPortfolioHistoryData,
  selectDisplayCurrency,
  selectPortfolioHistoryGraphPrecision,
  (consolidatedPortfolioHistoryData, displayCurrency, precisionConfig) => {
    const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
    const precision = get(precisionConfig, ["balance", displayCurrency], 2);
    return formatTimeSeriesChartData(
      consolidatedPortfolioHistoryData,
      yValueIndex,
      true,
      null,
      precision
    );
  }
);

export const selectCoinAccountsData = (
  args: GetCoinExtraExchangesDataRequest | SkipToken
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getCoinExtraExchangesData.select(
      args
    ),
    ({ data }) => {
      const exchangeToClientsMapping = getExchangesToClientsMapping(
        data?.filters
      );
      return Object.entries(exchangeToClientsMapping).reduce(
        (acc: ProcessedCoinExchangesData[], [exchange, clients]) => {
          clients?.forEach((client) => {
            const coin = data?.data?.[exchange]?.[client];
            acc.push({
              ...coin,
              client,
              exchange,
            } as ProcessedCoinExchangesData);
          });
          return acc;
        },
        []
      );
    }
  );

export const selectUniqueCoinAccountsData = (
  args: GetCoinExtraExchangesDataRequest | SkipToken
) => {
  return createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getCoinExtraExchangesData.select(
      args
    ),
    selectCoinAccountsData(args),
    ({ data }, accountsData) => {
      return dedupeAccountsData(accountsData, data?.client_dependency_new);
    }
  );
};

export const selectCoinAccountsPrecision = (
  args: GetCoinExtraExchangesDataRequest | SkipToken
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getCoinExtraExchangesData.select(
      args
    ),
    ({ data }) => data?.precision
  );

// Account extra details
export const selectAccountExtraInfoGeneralPrecision = (
  args: GetAccountExtraInfoGeneralRequest | SkipToken
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getAccountExtraInfoGeneral.select(
      args
    ),
    ({ data }) => data?.precision
  );

export const selectAccountExtraInfoGraphTotalData = (
  args: GetAccountExtraInfoGeneralRequest
) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getAccountExtraInfoGeneral.select(
      args
    ),
    selectDisplayCurrency,
    ({ data }, displayCurrency) => {
      const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 0;
      const totalData = data?.data?.total || [0, 0];
      return totalData[yValueIndex];
    }
  );
